// app/src/room-types.js
// Pure helpers for room-type identity and display labels.
// Mirrored to booking/src/room-types.js — keep in sync manually.

export const BASELINE_TYPES_EN = {
  room: 'Room',
  apartment: 'Apartment',
  studio: 'Studio',
  suite: 'Suite',
  villa: 'Villa',
  bungalow: 'Bungalow',
};

export const BASELINE_TYPES_BG = {
  room: 'Стая',
  apartment: 'Апартамент',
  studio: 'Студио',
  suite: 'Сюит',
  villa: 'Вила',
  bungalow: 'Бунгало',
};

// Turn an owner-typed label into a stable kebab-case key.
// Returns "custom-N" when slugification produces an empty string (e.g. Cyrillic input),
// where N is the lowest number greater than the highest existing `custom-N` key
// (so that previously-deleted keys are never reused — keeps history references stable).
// A label that slugifies to a baseline key (e.g. "Studio" → "studio") merges into
// that baseline naturally — no special branch needed.
export function slugifyType(label, existingCustomKeys = []) {
  const slug = String(label || '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  if (!slug) {
    const usedNumbers = existingCustomKeys
      .map(k => k.match(/^custom-(\d+)$/))
      .filter(Boolean)
      .map(m => Number(m[1]));
    const next = usedNumbers.length === 0 ? 1 : Math.max(...usedNumbers) + 1;
    return `custom-${next}`;
  }

  return slug;
}

// Resolve the display label for a room type.
// Order: custom labels[lang] → custom labels[other lang] → baseline[lang] → capitalize fallback.
export function resolveTypeLabel(type, roomTypeInfo, lang) {
  if (!type) return '';
  const info = roomTypeInfo || {};
  const labels = info[type]?.labels;

  if (labels?.[lang]) return labels[lang];
  if (labels) {
    // any other language present
    const fallback = Object.values(labels).find(v => typeof v === 'string' && v.length > 0);
    if (fallback) return fallback;
  }

  if (lang === 'bg' && BASELINE_TYPES_BG[type]) return BASELINE_TYPES_BG[type];
  if (BASELINE_TYPES_EN[type]) return BASELINE_TYPES_EN[type];

  return type.charAt(0).toUpperCase() + type.slice(1);
}

// Build the dropdown list of types this property knows about: the 6 baselines
// followed by any custom types already present in room_type_info.
export function getKnownTypeKeys(property) {
  const info = property?.room_type_info || {};
  const customKeys = Object.keys(info).filter(k => !BASELINE_TYPES_EN[k]);
  return [...Object.keys(BASELINE_TYPES_EN), ...customKeys];
}
